// ui/src/common/ModuleTile.tsx
//
// Wrapper that applies the "locked" treatment to a landing tile. Your existing
// <Tile> component is untouched — wrap it.
//
//   <ModuleTile module="DQ" state={stateOf('DQ')} onRequestAccess={handleRequest}>
//     <Tile idx="01 / DQ" ... />
//   </ModuleTile>

import React from "react";
import { ModuleCode, ModuleState } from "../hooks/useModuleAccess";

interface Props {
  module: ModuleCode;
  state: ModuleState;
  children: React.ReactNode;
  onRequestAccess?: (m: ModuleCode) => void;
}

export default function ModuleTile({ module, state, children, onRequestAccess }: Props) {
  // Not installed in this environment — nobody can use it, so render nothing.
  if (state === "not-deployed") return null;

  // Available — render the real tile untouched.
  if (state === "available") return <>{children}</>;

  // Installed here but not granted to this user: visible, muted, not actionable,
  // with the reason on the card and a route forward.
  return (
    <div className="gwl-tile-locked" aria-disabled="true">
      <div className="gwl-tile-locked-inner">{children}</div>

      <div className="gwl-tile-locked-overlay">
        <div className="gwl-tile-locked-badge">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="2" />
            <path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <span>Access not granted</span>
        </div>

        <p className="gwl-tile-locked-msg">
          This module is available in this environment but has not been granted to
          your account.
        </p>

        {onRequestAccess && (
          <button
            type="button"
            className="gwl-tile-locked-btn"
            onClick={() => onRequestAccess(module)}
          >
            Request access
          </button>
        )}
      </div>
    </div>
  );
}
